import axios from 'axios'
import { getActivePinia } from 'pinia'
import { useUiStore } from '@/stores/ui'
import { activeOrganizationId } from '@/stores/portalOrganizations'

declare module 'axios' {
  interface AxiosRequestConfig {
    skipToast?: boolean
  }
}

let isRedirecting = false

const client = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,
})

client.interceptors.request.use((config) => {
  if (config.url?.startsWith('/portal/')) {
    const orgId = activeOrganizationId()
    if (orgId) config.headers.set('X-Portal-Organization', orgId)
  }
  return config
})

export function extractErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { message?: string } } })?.response?.data
  return data?.message || 'Ha ocurrido un error inesperado'
}

client.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = (error as { response?: { status?: number } })?.response?.status

    if (status === 401) {
      const requestUrl = (error as { config?: { url?: string } })?.config?.url
      const sessionProbeUrls = ['/session', '/session/discover', '/portal/session']
      const isLoginRequest = sessionProbeUrls.includes(requestUrl ?? '')
      if (!isLoginRequest && !isRedirecting) {
        isRedirecting = true
        const isPortal = requestUrl?.startsWith('/portal/')
        if (isPortal) {
          window.location.href = '/portal/login'
        } else {
          window.location.href = '/login'
        }
      }
      return Promise.reject(error)
    }

    const toastMessages: Record<number, string> = {
      400: 'Solicitud invalida',
      403: 'No tienes permisos para realizar esta acción',
      404: 'El recurso solicitado no fue encontrado',
      422: extractErrorMessage(error),
      500: 'Error interno del servidor. Intenta nuevamente más tarde',
    }

    const skipToast = error?.config?.skipToast
    if (status && status in toastMessages && !skipToast) {
      const message = status === 422 ? extractErrorMessage(error) : toastMessages[status]
      if (getActivePinia()) useUiStore().showToast(message, 'error')
    }

    return Promise.reject(error)
  },
)

export default client
